import { useEffect, useMemo, useState } from 'react';
import moment from 'moment/moment';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getSelectedTasklistForAction } from '../../../../store/tasklists/selectors';
import { getMembers } from '../../../../store/team/selectors';
import { INITIAL_TASKLIST } from '../../Shared/data';
import { TEditTasklist, TMember } from '../../../../types';
import { getDiffBetweenNestedObj } from '../../../../utils/helpers';

export function useEditTasklist() {
  const selectedTasklistForAction = useAppSelector(getSelectedTasklistForAction);
  const members = useAppSelector(getMembers);
  const { t } = useTranslation('common');

  const [formInputs, setFormInputs] = useState(INITIAL_TASKLIST);
  const [prevFormInputs, setPrevFormInputs] = useState(INITIAL_TASKLIST);

  const [mode, setMode] = useState('create');

  useEffect(() => {
    if (selectedTasklistForAction.id) {
      setMode('edit');

      const assign = members.find((member) => member.email === selectedTasklistForAction.assigneeEmail) || {} as TMember;
      const date = selectedTasklistForAction.date ? moment(selectedTasklistForAction.date) : null;

      const tasklistData = {
        locations: selectedTasklistForAction.locations,
        visibility: Array.isArray(selectedTasklistForAction.visibility)
        && selectedTasklistForAction.visibility.length === 1 && selectedTasklistForAction.visibility[0] === null
          ? []
          : selectedTasklistForAction.visibility.map((item) => ({
            id: item,
            name: t(`${item}`),
          })),
        name: selectedTasklistForAction.title,
        assign,
        description: selectedTasklistForAction.description || '',
        tasks: selectedTasklistForAction.tasks,
        notifyAssignee: false,
        isAssigned: selectedTasklistForAction.isAssigned,
        isDated: selectedTasklistForAction.isDated,
        date,
      } as unknown as TEditTasklist;

      setFormInputs(tasklistData);
      setPrevFormInputs(tasklistData);
    } else {
      setMode('create');
      setFormInputs(INITIAL_TASKLIST);
      setPrevFormInputs(INITIAL_TASKLIST);
    }
  }, [selectedTasklistForAction]);

  const getFormInputs = useMemo(() => formInputs, [formInputs]);

  const isEdited = useMemo(() => {
    const diff = getDiffBetweenNestedObj(formInputs, prevFormInputs);

    return !isEmpty(diff);
  }, [formInputs, prevFormInputs]);

  return {
    mode,
    formInputs,
    isEdited,
    setFormInputs,
    getFormInputs,
  };
}
